import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { ExternalLink, LinkIcon, Pencil, Unlink } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface LinkBubbleMenuProps {
  editor: Editor;
}

export function LinkBubbleMenu({ editor }: LinkBubbleMenuProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editUrl, setEditUrl] = useState("");

  // Get current link URL from editor
  const getCurrentUrl = useCallback(() => {
    const attrs = editor.getAttributes("link");
    return attrs.href || "";
  }, [editor]);

  // Reset edit state when selection changes
  useEffect(() => {
    const handleSelectionChange = () => {
      setIsEditing(false);
      setEditUrl("");
    };

    editor.on("selectionUpdate", handleSelectionChange);
    return () => {
      editor.off("selectionUpdate", handleSelectionChange);
    };
  }, [editor]);

  // Start editing with current URL
  const handleEdit = useCallback(() => {
    setEditUrl(getCurrentUrl());
    setIsEditing(true);
  }, [getCurrentUrl]);

  // Cancel editing
  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditUrl("");
    editor.chain().focus().run();
  }, [editor]);

  // Save the edited link
  const handleSave = useCallback(() => {
    if (!editUrl.trim()) {
      // If URL is empty, remove the link
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      // Add https:// if no protocol specified
      let url = editUrl.trim();
      if (!url.startsWith("http://") && !url.startsWith("https://") && !url.startsWith("mailto:")) {
        url = "https://" + url;
      }
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setIsEditing(false);
    setEditUrl("");
  }, [editor, editUrl]);

  // Remove the link
  const handleUnlink = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
  }, [editor]);

  // Open link in new tab
  const handleOpen = useCallback(() => {
    const url = getCurrentUrl();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [getCurrentUrl]);

  // Handle keyboard shortcuts in edit mode
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  // Truncate URL for display
  const truncateUrl = (url: string, maxLength = 40) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  return (
    <BubbleMenu
      editor={editor}
      options={{
        placement: "bottom-start",
        offset: 8,
      }}
      shouldShow={({ editor }) => {
        // Only show when cursor is on a link
        return editor.isActive("link");
      }}
      className="link-bubble-menu"
    >
      <div className="bg-card border border-border rounded-lg shadow-lg p-2 flex items-center gap-2 min-w-[280px]">
        {isEditing ? (
          // Edit mode
          <div className="flex items-center gap-2 w-full">
            <input
              type="text"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter URL..."
              className="flex-1 text-sm px-2 py-1.5 border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
            <button
              onClick={handleSave}
              className="px-2 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent rounded"
            >
              Cancel
            </button>
          </div>
        ) : (
          // Display mode
          <>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground mb-0.5 flex items-center gap-1">
                <LinkIcon className="h-3 w-3" />
                Link URL
              </div>
              <div className="text-sm text-foreground truncate" title={getCurrentUrl()}>
                {truncateUrl(getCurrentUrl())}
              </div>
            </div>
            <div className="flex items-center gap-1 border-l border-border pl-2">
              <button
                onClick={handleEdit}
                className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                title="Edit link"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={handleOpen}
                className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
              <button
                onClick={handleUnlink}
                className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
                title="Remove link"
              >
                <Unlink className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </BubbleMenu>
  );
}
